import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Calendar, MapPin, Building2, Globe, ArrowLeft, ExternalLink } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function OrganizerPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [organizer, setOrganizer] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/organizers/${id}`)
      .then(r => setOrganizer(r.data))
      .catch(() => navigate("/organizers"))
      .finally(() => setLoading(false));
  }, [id, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-500" />
      </div>
    );
  }

  if (!organizer) return null;

  return (
    <div className="min-h-screen bg-gray-50 text-gray-900">
      <nav className="border-b border-gray-200 bg-white sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2 cursor-pointer" onClick={() => navigate("/")}>
            <Calendar className="h-6 w-6 text-blue-500" />
            <span className="text-xl font-bold">ExpoPulse <span className="text-blue-500">Events</span></span>
          </div>
          <div className="flex items-center gap-6">
            <button onClick={() => navigate("/exhibitions")} className="text-gray-600 hover:text-gray-900 transition text-sm">Exhibitions</button>
            <button onClick={() => navigate("/organizers")} className="text-gray-600 hover:text-gray-900 transition text-sm">Organizers</button>
          </div>
        </div>
      </nav>

      <div className="max-w-4xl mx-auto px-4 py-8">
        <button onClick={() => navigate(-1)} className="flex items-center gap-1 text-gray-500 hover:text-gray-900 mb-6 text-sm transition">
          <ArrowLeft className="h-4 w-4" /> Back
        </button>

        <div className="flex items-start gap-6 mb-8">
          {organizer.logo_url && (
            <img src={organizer.logo_url} alt="" className="w-20 h-20 rounded-xl object-cover bg-white shrink-0" onError={(e) => e.target.style.display='none'} />
          )}
          <div>
            <h1 className="text-3xl font-bold mb-2">{organizer.name}</h1>
            {organizer.category && (
              <span className="inline-block bg-blue-50 text-blue-600 text-sm px-3 py-1 rounded mb-3">{organizer.category}</span>
            )}
            <div className="flex flex-wrap gap-4 text-sm text-gray-500">
              {organizer.country && (
                <div className="flex items-center gap-1"><MapPin className="h-4 w-4" /> {[organizer.city, organizer.country].filter(Boolean).join(", ")}</div>
              )}
              {organizer.region && (
                <div className="flex items-center gap-1"><Globe className="h-4 w-4" /> {organizer.region}</div>
              )}
              {organizer.website && (
                <a href={organizer.website} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-blue-600 hover:text-blue-700">
                  <ExternalLink className="h-4 w-4" /> Website
                </a>
              )}
            </div>
          </div>
        </div>

        {organizer.description && (
          <div className="mb-8">
            <h2 className="text-base font-semibold mb-3">About</h2>
            <p className="text-gray-600 leading-relaxed">{organizer.description}</p>
          </div>
        )}

        {/* Exhibitions by this organizer */}
        {organizer.exhibitions?.length > 0 && (
          <div>
            <h2 className="text-base font-semibold mb-4 flex items-center gap-2">
              <Calendar className="h-5 w-5 text-blue-500" />
              Exhibitions ({organizer.exhibitions.length})
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {organizer.exhibitions.map((ex) => (
                <div key={ex._id} onClick={() => navigate(`/exhibition/${ex._id}`)}
                  className="bg-white border border-gray-200 rounded-lg p-4 hover:border-blue-300 hover:shadow-sm transition cursor-pointer group">
                  <h3 className="font-medium group-hover:text-blue-600 transition">{ex.name}</h3>
                  {ex.industry && <div className="text-xs text-blue-600 mt-1">{ex.industry}</div>}
                  <div className="flex items-center gap-1 text-sm text-gray-500 mt-2">
                    <MapPin className="h-3.5 w-3.5" /> {[ex.city, ex.country].filter(Boolean).join(", ")}
                  </div>
                  {ex.start_date && (
                    <div className="text-sm text-gray-500 mt-1">{ex.start_date}</div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
